/**
 * Tenant 로그인 전용 페이지
 * @description /login — Tenant 사용자만, 로그인 후 /entry
 */

import { useState } from 'react';
import { useNavigate, getQueryParam } from '@shared/router';
import { useAuthStore } from '@/stores/authStore';
import { changeTenantPassword } from '@/services/authApi';
import { navigateAfterAuth } from '@/utils/mcpOAuth';
import { resolvePostLoginLanding } from '@/utils/executiveLanding';
import { PORTAL_VERSION_FOOTER } from '@shared/config';
import { Input, Button } from '@shared/ui/primitives';
import { Eye, EyeOff, ShieldCheck, Lock } from 'lucide-react';
import { AuthLoginShell } from '@/features/auth/components/AuthLoginShell';
import { usePortalTranslation } from '@shared/i18n';

type LoginStep = 'password' | 'mfa' | 'changePassword';

const inputClass =
  'w-full h-10 rounded-lg border-slate-200/80 bg-white/70 shadow-sm focus:border-primary/40 focus:ring-2 focus:ring-primary/15 transition-all duration-200';

export const TenantLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = usePortalTranslation('auth');
  const login = useAuthStore((state) => state.login);
  const completeMfa = useAuthStore((state) => state.completeMfa);
  const returnUrl = getQueryParam('returnUrl');
  const navigateToService = useAuthStore((state) => state.navigateToService);
  const goAfterLogin = () => {
    const user = useAuthStore.getState().user;
    const hint =
      user && user.userType === 'tenant-user'
        ? resolvePostLoginLanding({
            roles: user.roles,
            teamIds: user.teamIds,
            userId: user.userId,
            userType: user.userType,
          })
        : null;
    navigateAfterAuth(returnUrl, navigate, navigateToService, hint);
  };

  const [tenantId, setTenantId] = useState('');
  const [userId, setUserId] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [step, setStep] = useState<LoginStep>('password');
  const [mfaChallengeId, setMfaChallengeId] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
  const [changePasswordFromMfa, setChangePasswordFromMfa] = useState(false);
  const [currentPasswordForChange, setCurrentPasswordForChange] = useState('');

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    try {
      const result = await login({
        mode: 'tenant-user',
        tenantId,
        userId,
        password,
      });
      if ('requireMfa' in result && result.requireMfa) {
        setMfaChallengeId(result.mfaChallengeId);
        setStep('mfa');
        setMfaCode('');
        return;
      }
      if ('mustChangePassword' in result && result.mustChangePassword === true) {
        setChangePasswordFromMfa(false);
        setNewPassword('');
        setNewPasswordConfirm('');
        setStep('changePassword');
        return;
      }
      goAfterLogin();
    } catch (err) {
      setError(err instanceof Error ? err.message : '로그인에 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMfaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!mfaCode.trim()) {
      setError('인증 코드 6자리를 입력하세요.');
      return;
    }
    setIsLoading(true);
    try {
      const mfaResult = await completeMfa(mfaChallengeId, mfaCode);
      if (mfaResult?.mustChangePassword === true) {
        setChangePasswordFromMfa(true);
        setCurrentPasswordForChange('');
        setNewPassword('');
        setNewPasswordConfirm('');
        setStep('changePassword');
        return;
      }
      goAfterLogin();
    } catch (err) {
      setError(err instanceof Error ? err.message : '인증 코드가 올바르지 않거나 만료되었습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  const validateNewPassword = (p: string): string | null => {
    if (p.length < 8) return '비밀번호는 8자 이상이어야 합니다.';
    if (!/[A-Z]/.test(p)) return '대문자를 1자 이상 포함해야 합니다.';
    if (!/[a-z]/.test(p)) return '소문자를 1자 이상 포함해야 합니다.';
    if (!/\d/.test(p)) return '숫자를 1자 이상 포함해야 합니다.';
    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(p)) return '특수문자를 1자 이상 포함해야 합니다.';
    return null;
  };

  const handleChangePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const currentPw = changePasswordFromMfa ? currentPasswordForChange : password;
    if (!currentPw.trim()) {
      setError('현재 비밀번호를 입력하세요.');
      return;
    }
    if (!newPassword.trim()) {
      setError('새 비밀번호를 입력하세요.');
      return;
    }
    if (newPassword !== newPasswordConfirm) {
      setError('새 비밀번호가 일치하지 않습니다.');
      return;
    }
    const err = validateNewPassword(newPassword);
    if (err) {
      setError(err);
      return;
    }
    setIsLoading(true);
    try {
      await changeTenantPassword(currentPw, newPassword);
      goAfterLogin();
    } catch (err) {
      setError(err instanceof Error ? err.message : '비밀번호 변경에 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  const title =
    step === 'mfa'
      ? t('tenantLogin.titleMfa')
      : step === 'changePassword'
        ? t('tenantLogin.titleChangePassword')
        : t('tenantLogin.title');

  const subtitle =
    step === 'mfa'
      ? t('tenantLogin.subtitleMfa')
      : step === 'changePassword'
        ? t('tenantLogin.subtitleChangePassword')
        : t('tenantLogin.subtitle');

  return (
    <AuthLoginShell>
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-slate-900 tracking-tight">{title}</h2>
        <p className="mt-1.5 text-sm text-slate-500">{subtitle}</p>
      </div>
      {error && (
        <div className="mb-5 p-3 rounded-lg bg-red-50/90 border border-red-200/80 backdrop-blur-sm" role="alert">
          <p className="text-sm text-red-700 font-medium">{error}</p>
        </div>
      )}

      {step === 'password' && (
        <form onSubmit={handlePasswordSubmit} className="flex flex-col gap-4">
          <div className="space-y-1.5">
            <label htmlFor="tenantId" className="block text-sm font-medium text-slate-700">
              {t('login.tenantId')}
            </label>
            <Input
              id="tenantId"
              type="text"
              placeholder="iampam"
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value)}
              disabled={isLoading}
              className={inputClass}
            />
          </div>
          <div className="space-y-1.5">
            <label htmlFor="userId" className="block text-sm font-medium text-slate-700">
              {t('tenantLogin.userId')}
            </label>
            <Input
              id="userId"
              type="text"
              placeholder={t('tenantLogin.userIdPlaceholder')}
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              disabled={isLoading}
              className={inputClass}
            />
          </div>
          <div className="space-y-1.5">
            <label htmlFor="password" className="block text-sm font-medium text-slate-700">
              {t('tenantLogin.password')}
            </label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder={t('tenantLogin.passwordPlaceholder')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoading}
                className={`${inputClass} pr-10`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                aria-label={showPassword ? t('tenantLogin.hidePassword') : t('tenantLogin.showPassword')}
              >
                {showPassword ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
              </button>
            </div>
            <p className="text-xs text-slate-500">{t('tenantLogin.passwordPolicy')}</p>
          </div>
          <Button
            type="submit"
            variant="default"
            disabled={isLoading}
            size="sm"
            className="w-full h-11 mt-1 rounded-lg font-medium shadow-md shadow-primary/15"
          >
            {isLoading ? (
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                {t('tenantLogin.submitting')}
              </span>
            ) : (
              t('tenantLogin.submit')
            )}
          </Button>
        </form>
      )}

      {step === 'changePassword' && (
        <form onSubmit={handleChangePasswordSubmit} className="flex flex-col gap-4">
          <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-50/90 border border-amber-200/80 text-amber-800 backdrop-blur-sm">
            <Lock className="w-5 h-5 shrink-0" />
            <span className="text-sm font-medium">{t('tenantLogin.changePasswordBanner')}</span>
          </div>
          {changePasswordFromMfa && (
            <div className="space-y-1.5">
              <label htmlFor="currentPasswordForChange" className="block text-sm font-medium text-slate-700">
                {t('tenantLogin.currentPassword')}
              </label>
              <Input
                id="currentPasswordForChange"
                type="password"
                value={currentPasswordForChange}
                onChange={(e) => setCurrentPasswordForChange(e.target.value)}
                disabled={isLoading}
                placeholder={t('tenantLogin.currentPasswordPlaceholder')}
                className={inputClass}
              />
            </div>
          )}
          <div className="space-y-1.5">
            <label htmlFor="newPassword" className="block text-sm font-medium text-slate-700">
              {t('tenantLogin.newPassword')}
            </label>
            <Input
              id="newPassword"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              disabled={isLoading}
              placeholder={t('tenantLogin.newPasswordPlaceholder')}
              className={inputClass}
            />
            <p className="text-xs text-slate-500">{t('tenantLogin.passwordPolicyStrict')}</p>
          </div>
          <div className="space-y-1.5">
            <label htmlFor="newPasswordConfirm" className="block text-sm font-medium text-slate-700">
              {t('tenantLogin.confirmNewPassword')}
            </label>
            <Input
              id="newPasswordConfirm"
              type="password"
              value={newPasswordConfirm}
              onChange={(e) => setNewPasswordConfirm(e.target.value)}
              disabled={isLoading}
              placeholder={t('tenantLogin.confirmNewPasswordPlaceholder')}
              className={inputClass}
            />
          </div>
          <div className="flex gap-2 mt-1">
            <Button
              type="button"
              variant="outline"
              disabled={isLoading}
              size="sm"
              className="flex-1 rounded-lg h-11 bg-white/50"
              onClick={() => {
                setStep(changePasswordFromMfa ? 'mfa' : 'password');
                setError(null);
                setNewPassword('');
                setNewPasswordConfirm('');
              }}
            >
              {t('tenantLogin.back')}
            </Button>
            <Button
              type="submit"
              variant="default"
              disabled={isLoading || !newPassword || newPassword !== newPasswordConfirm}
              size="sm"
              className="flex-1 rounded-lg h-11 shadow-md shadow-primary/15"
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  {t('tenantLogin.changingPassword')}
                </span>
              ) : (
                t('tenantLogin.changePasswordSubmit')
              )}
            </Button>
          </div>
        </form>
      )}

      {step === 'mfa' && (
        <form onSubmit={handleMfaSubmit} className="flex flex-col gap-4">
          <div className="flex items-center gap-2 p-3 rounded-lg bg-primary/10 border border-primary/15 text-primary backdrop-blur-sm">
            <ShieldCheck className="w-5 h-5 shrink-0" />
            <span className="text-sm font-medium">{t('tenantLogin.mfaBanner', { userId })}</span>
          </div>
          <div className="space-y-1.5">
            <label htmlFor="mfaCode" className="block text-sm font-medium text-slate-700">
              {t('tenantLogin.mfaCode')}
            </label>
            <Input
              id="mfaCode"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="000000"
              maxLength={8}
              value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              disabled={isLoading}
              className={`${inputClass} h-11 text-center text-lg tracking-[0.4em] font-mono`}
            />
          </div>
          <div className="flex gap-2 mt-1">
            <Button
              type="button"
              variant="outline"
              disabled={isLoading}
              size="sm"
              className="flex-1 rounded-lg h-11 bg-white/50"
              onClick={() => { setStep('password'); setError(null); setMfaCode(''); }}
            >
              {t('tenantLogin.back')}
            </Button>
            <Button
              type="submit"
              variant="default"
              disabled={isLoading || mfaCode.length < 6}
              size="sm"
              className="flex-1 rounded-lg h-11 shadow-md shadow-primary/15"
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  {t('tenantLogin.mfaSubmitting')}
                </span>
              ) : (
                t('tenantLogin.mfaSubmit')
              )}
            </Button>
          </div>
        </form>
      )}
      <p className="text-xs text-slate-500 text-center mt-6">
        {t('tenantLogin.adminLoginLink')}{' '}
        <a href="/admin/login" className="text-primary hover:underline font-medium">
          /admin/login
        </a>{' '}
        {t('tenantLogin.adminLoginSuffix')}
      </p>
      <p className="text-[11px] text-slate-400 text-center mt-2 tracking-wide">
        {PORTAL_VERSION_FOOTER}
      </p>
    </AuthLoginShell>
  );
};

TenantLoginPage.displayName = 'TenantLoginPage';
